import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilder, usePersistedForm } from '@gov-nx/core/service';
import { useSmazaniUctuLocale } from '@gov-nx/module/service';
import type { FormCheckboxProps, FormInputProps } from '@gov-nx/ui/types';
import {
	getCheckboxDefinition,
	useInputDefinition,
} from '@gov-nx/utils/common';

interface Step1Fields {
	agreeWithDeletion: FormCheckboxProps;
}

interface FormStep1 {
	agreeWithDeletion: boolean;
}

export type SmazaniUctuFormStep1 = FormDefinition2<FormStep1, Step1Fields>;

export const useFormStep1 = ({
	isDisabled,
}: {
	isDisabled: boolean;
}): SmazaniUctuFormStep1 => {
	const { t } = useSmazaniUctuLocale();

	const agreeWithDeletion = getCheckboxDefinition({
		name: 'agreeWithDeletion',
		isDisabled,
		oneOf: {
			value: [true],
			errorMessage: t('formular.souhlas-se-smazanim.validace.povinny'),
		},
		label: t('formular.souhlas-se-smazanim.label'),
	});

	const formDefinition = useFormBuilder<FormStep1, Step1Fields>([
		agreeWithDeletion,
	]);

	return usePersistedForm('smazani-uctu-step1')(formDefinition);
};

interface Step2Fields {
	agreeWithKeepingSomeInfo: FormCheckboxProps;
}

interface FormStep2 {
	agreeWithKeepingSomeInfo: boolean;
}

export type SmazaniUctuFormStep2 = FormDefinition2<FormStep2, Step2Fields>;

export const useFormStep2 = ({
	isDisabled,
}: {
	isDisabled: boolean;
}): SmazaniUctuFormStep2 => {
	const { t } = useSmazaniUctuLocale();

	const agreeWithKeepingSomeInfo = getCheckboxDefinition({
		name: 'agreeWithKeepingSomeInfo',
		isDisabled,
		oneOf: {
			value: [true],
			errorMessage: t(
				'formular.souhlas-s-ponechanim-informaci.validace.povinny'
			),
		},
		label: t('formular.souhlas-s-ponechanim-informaci.label'),
	});

	const formDefinition = useFormBuilder<FormStep2, Step2Fields>([
		agreeWithKeepingSomeInfo,
	]);

	return usePersistedForm('smazani-uctu-step2')(formDefinition);
};

interface Step3Fields {
	message: FormInputProps;
}

interface FormStep3 {
	message: string;
}

export type SmazaniUctuFormStep3 = FormDefinition2<FormStep3, Step3Fields>;

export const useFormStep3 = ({
	isDisabled,
}: {
	isDisabled: boolean;
}): SmazaniUctuFormStep3 => {
	const { t } = useSmazaniUctuLocale();

	const message = useInputDefinition({
		name: 'message',
		placeholder: t('formular.zprava.placeholder'),
		isDisabled,
		label: t('formular.zprava.label'),
		isMultiline: true,
		rows: 4,
	});

	const formDefinition = useFormBuilder<FormStep3, Step3Fields>([message]);

	return usePersistedForm('smazani-uctu-step3')(formDefinition);
};

export type SmazaniUctuFormData = FormStep1 & FormStep2 & FormStep3;
